import useSolarLightReach from "~/api/hooks/useSolarLightReach"
import useSolarProposalFinancing from "~/api/hooks/useSolarProposalFinancing"

import { CatalogLoanProductPicker } from "../../ProposalSetup/financing/CatalogLoanProductPicker"
import { solarLenderOf } from "../solar-finance-partner"
import {
  solarLoanProductBundle,
  solarSungageLoanProductBundle,
} from "../solar-financing-bundle"
import { SolarLightReachOfferPanel } from "../SolarLightReachOfferPanel"
import {
  LOAN_PRODUCT_LOAD_ERROR,
  LOAN_PRODUCT_NOT_REQUIRED_PROMPT,
} from "../SolarLoanProductStep"
import { CONFIRM_AND_OPEN_LABEL } from "./useSolarSummaryConfirm"

export const SUMMARY_LOAN_PRODUCT_SAVED_LABEL = `Loan Product saved. Confirm & open calculates the payment.`

/** Here Confirm & open runs Refresh Pricing, so the saved notice says so. */
const summaryBundle = (
  bundle: typeof solarLoanProductBundle,
): typeof solarLoanProductBundle => ({
  loanProduct: {
    ...bundle.loanProduct,
    copy: {
      ...bundle.loanProduct.copy,
      savedLabel: SUMMARY_LOAN_PRODUCT_SAVED_LABEL,
    },
  },
})
const summaryLoanProductBundle = summaryBundle(solarLoanProductBundle)
const summarySungageLoanProductBundle = summaryBundle(
  solarSungageLoanProductBundle,
)

/**
 * The Summary's Loan Product step. Credit Human or Sungage (a product of its
 * funding group): the shared catalog picker over the Solar financing read;
 * Confirm & open waits for a saved Loan Product that can still be chosen, as
 * the confirm requires. LightReach: Get LightReach pricing and its offers;
 * Confirm & open waits for an offer chosen at the current price. It follows
 * Equipment, whose save stores the plan the selection needs.
 */
export const SolarSummaryLoanProductStep = ({
  proposalId,
  onConfirm,
  busy,
}: {
  proposalId: string
  onConfirm: () => void
  /** Confirm & open is running. */
  busy: boolean
}): JSX.Element => {
  const financingQuery = useSolarProposalFinancing(proposalId)
  const financing = financingQuery.data ?? null
  const lender = solarLenderOf(financing)
  const lightReach = lender === `lightreach`
  const lightReachRead = useSolarLightReach(proposalId, {
    enabled: lightReach,
  }).data
  const ready = lightReach
    ? lightReachRead?.selectedOffer?.quoteStatus === `current`
    : financing?.selectionStatus === `loan_product_selected` &&
      financing.selectedLoanProduct?.availability === `eligible`

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="solar-summary-loan-product-step"
    >
      {financingQuery.isError ? (
        <div role="alert" className="flex items-center gap-3">
          <p className="text-[length:var(--step--1)] text-bp-danger">
            {LOAN_PRODUCT_LOAD_ERROR}
          </p>
          <button
            type="button"
            className="text-[length:var(--step--1)] font-semibold text-bp-accent underline"
            onClick={() => void financingQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : financing != null &&
        !financing.loanProductRequired &&
        !financingQuery.isFetching ? (
        <p className="text-[length:var(--step--1)] text-bp-text-muted">
          {LOAN_PRODUCT_NOT_REQUIRED_PROMPT}
        </p>
      ) : null}
      {lightReach ? (
        <SolarLightReachOfferPanel proposalId={proposalId} />
      ) : (
        <CatalogLoanProductPicker
          bundle={
            lender === `sungage`
              ? summarySungageLoanProductBundle
              : summaryLoanProductBundle
          }
          proposalId={proposalId}
          financing={financing?.loanProductRequired ? financing : null}
        />
      )}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="solar-summary-continue"
          className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          disabled={!ready || busy}
          onClick={onConfirm}
        >
          {CONFIRM_AND_OPEN_LABEL}
        </button>
      </div>
    </div>
  )
}
